import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { WizardTrigger } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import { selectGpuGuidanceEnabled } from "slices/explorer";

import {
    consumeGuidanceGhostNudge,
    getGuidanceWizardOpenToken,
    getGuidanceWizardTrigger,
    openGuidanceWizard,
    subscribeGuidanceSnapshot,
} from "../guidanceStore";
import { useGpuDetection } from "../useGpuDetection";
import { useGuidanceSnapshot } from "../useGuidanceSnapshot";
import { GuidanceBanner } from "./GuidanceBanner";
import { GuidanceDialog } from "./GuidanceDialog";
import { getGuidancePreview } from "./guidancePreview";
import { IntroPrompt } from "./IntroPrompt";

export function GpuDetectionGuidance() {
    const preview = getGuidancePreview();
    const gpuGuidanceEnabled = useAppSelector(selectGpuGuidanceEnabled);
    useGpuDetection(gpuGuidanceEnabled && !preview);

    const snapshot = useGuidanceSnapshot();
    const [forceSteps, setForceSteps] = useState(false);

    // Track wizard open calls: initialize ref to current token so calls that
    // happened before this component mounted do not retroactively open the dialog.
    const lastTokenRef = useRef(getGuidanceWizardOpenToken());
    const wizardToken = useSyncExternalStore(subscribeGuidanceSnapshot, getGuidanceWizardOpenToken, () => 0);
    useEffect(() => {
        if (wizardToken !== lastTokenRef.current) {
            lastTokenRef.current = wizardToken;
            setForceSteps(true);
        }
    }, [wizardToken]);

    // Ghost nudge: consume on first paint of the intro (situation B, Windows, not forced).
    useEffect(() => {
        if (
            gpuGuidanceEnabled &&
            snapshot?.lifecycle.surface === "wizard" &&
            !forceSteps &&
            snapshot.ghostNudgePending
        ) {
            void consumeGuidanceGhostNudge();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gpuGuidanceEnabled, snapshot?.lifecycle.surface, snapshot?.ghostNudgePending, forceSteps]);

    if (preview?.surface === "banner") {
        return <GuidanceBanner onTroubleshoot={() => undefined} />;
    }

    if (preview?.surface === "dialog") {
        return (
            <GuidanceDialog
                key={preview.view}
                initialView={preview.view}
                trigger={WizardTrigger.Auto}
                snapshot={preview.snapshot}
                initialExpandedKey={preview.expandedKey}
                onForceStepsDone={() => undefined}
            />
        );
    }

    if (!gpuGuidanceEnabled || !snapshot) {
        return null;
    }

    const onForceStepsDone = () => setForceSteps(false);

    if (forceSteps) {
        return (
            <GuidanceDialog
                key="steps"
                initialView="steps"
                trigger={getGuidanceWizardTrigger()}
                snapshot={snapshot}
                onForceStepsDone={onForceStepsDone}
            />
        );
    }

    switch (snapshot.lifecycle.surface) {
        case "wizard":
            return snapshot.isWindows ? (
                <GuidanceDialog
                    key="intro"
                    initialView="intro"
                    trigger={WizardTrigger.Auto}
                    snapshot={snapshot}
                    onForceStepsDone={onForceStepsDone}
                />
            ) : null;
        case "banner":
            return snapshot.isWindows ? (
                <GuidanceBanner onTroubleshoot={() => openGuidanceWizard(WizardTrigger.Troubleshoot)} />
            ) : null;
        case "hard-stop":
            // Not gated on engine init failing: with acceleration off Windows browsers fall
            // back to a software rasterizer, so the viewer "loads" and the user would
            // otherwise get no guidance at all.
            return (
                <IntroPrompt
                    isWindows={snapshot.isWindows}
                    accelerationOff
                    onGetStarted={snapshot.isWindows ? () => openGuidanceWizard(WizardTrigger.HardStop) : undefined}
                    testId="gpu-guidance-hard-stop"
                    preserveBodyLayout
                />
            );
        case "none":
            return null;
    }
}
